import React, { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import { Item, useCanvasStore } from '@/stores/canvasStore';
import { FileText, Download, Copy, MoreHorizontal } from 'lucide-react';
import { DocumentEditor } from '@/components/modals/DocumentEditor';
import { isPartnrV2Enabled } from '@/lib/partnr/flag';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { countWords, renderHtmlPreview } from '@/lib/htmlPreview';
import { cn } from '@/lib/utils';
import { usePartnrOpenDocumentStore } from '@/lib/partnr/openDocument';

// Loaded on first open, so the flag-off path ships none of it
const DocumentCanvas = lazy(() => import('@/components/doc/DocumentCanvas').then((m) => ({ default: m.DocumentCanvas })));

interface DocumentCardProps {
  item: Item;
}

export function DocumentCard({ item }: DocumentCardProps) {
  const content = item.content as { title?: string; body?: string; wordCount?: number; source?: string };
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  // Partnr 2 opens the full-screen document surface; flag off keeps the old editor
  const partnrV2 = useMemo(() => isPartnrV2Enabled(), []);

  // Partnr's create_document can ask for the editor to open, as a click would
  const openRequested = usePartnrOpenDocumentStore((s) => s.requestedId === item.id);
  useEffect(() => {
    if (openRequested && usePartnrOpenDocumentStore.getState().take(item.id)) setIsEditorOpen(true);
  }, [openRequested, item.id]);

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditorOpen(true);
  };

  const preview = useMemo(() => renderHtmlPreview(content.body), [content.body]);
  const wordCount = useMemo(
    () => content.wordCount || countWords(content.body),
    [content.wordCount, content.body],
  );
  const minutes = Math.max(1, Math.round(wordCount / 230));
  const kind = content.source === 'markdown' ? 'Markdown' : 'Document';
  // Short cards (older uploads were 120px) keep the header to one line so the preview shows
  const compact = item.height < 200;

  const getMarkdownContent = () => {
    return `# ${content.title || 'Untitled Document'}\n\n${content.body || ''}`;
  };

  const handleCopyMarkdown = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(getMarkdownContent());
    toast.success('Copied to clipboard');
  };

  const handleDownloadMarkdown = (e: React.MouseEvent) => {
    e.stopPropagation();
    const markdown = getMarkdownContent();
    const blob = new Blob([markdown], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${content.title || 'document'}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Downloaded');
  };

  const handleClick = (e: React.MouseEvent) => {
    // Single click in the main content area opens the editor
    // Only trigger if not clicking on the dropdown menu
    const target = e.target as HTMLElement;
    if (!target.closest('[data-radix-collection-item]') && !target.closest('button')) {
      e.stopPropagation();
      setIsEditorOpen(true);
    }
  };

  return (
    <>
      <div
        className="w-full h-full bg-card rounded-lg card-shadow overflow-hidden flex flex-col border border-border hover:border-primary/50 transition-colors group relative"
      >
        {/* Drag zones at top and bottom (20% each) - show grab cursor */}
        <div 
          className="absolute inset-x-0 top-0 h-[20%] z-20 cursor-grab"
          data-drag-zone="true"
        />
        <div 
          className="absolute inset-x-0 bottom-0 h-[20%] z-20 cursor-grab"
          data-drag-zone="true"
        />
        
        {/* Export menu - visible on hover */}
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-30">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-7 w-7 bg-background/80 backdrop-blur-sm"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleCopyMarkdown}>
                <Copy className="mr-2 h-4 w-4" />
                Copy as Markdown
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleDownloadMarkdown}>
                <Download className="mr-2 h-4 w-4" />
                Download as .md
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Content area - clickable to open editor */}
        <div 
          className="flex-1 min-h-0 cursor-pointer flex flex-col relative z-10"
          onClick={handleClick}
          onDoubleClick={handleDoubleClick}
        >
          <div className={cn('px-4 border-b border-border/60', compact ? 'pt-2.5 pb-2' : 'pt-3.5 pb-2.5')}>
            <div className="flex items-start gap-2 pr-8">
              <div className="mt-0.5 h-5 w-5 shrink-0 rounded-md bg-primary/10 flex items-center justify-center">
                <FileText className="w-3 h-3 text-primary" />
              </div>
              <h3
                className={cn(
                  'text-sm font-semibold leading-snug text-foreground break-words',
                  compact ? 'line-clamp-1' : 'line-clamp-2',
                )}
              >
                {content.title || 'Untitled Document'}
              </h3>
            </div>
            {!compact && (
              <div className="mt-1 pl-7 text-[11px] text-muted-foreground">
                {kind} · {wordCount} {wordCount === 1 ? 'word' : 'words'}
                {wordCount > 0 && ` · ${minutes} min read`}
              </div>
            )}
          </div>

          <div className="relative flex-1 min-h-0 overflow-hidden px-4 pt-2.5">
            {preview.length > 0 ? (
              <div className="doc-preview">{preview}</div>
            ) : (
              <p className="text-sm italic text-muted-foreground opacity-60">Click to edit...</p>
            )}
            {/* Fade the cut-off text into the card */}
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-card to-transparent" />
          </div>
        </div>
      </div>
      
      {partnrV2 ? (
        // Mounted only while open: one editor per open document, not per card
        isEditorOpen && (
          <Suspense fallback={null}>
            <DocumentCanvas item={item} open onOpenChange={setIsEditorOpen} />
          </Suspense>
        )
      ) : (
        <DocumentEditor
          item={item}
          isOpen={isEditorOpen}
          onClose={() => setIsEditorOpen(false)}
        />
      )}
    </>
  );
}
